/**
 * GIFT THRESHOLD CAMPAIGN (kaupanpäälliset)
 * ---------------------------------------------------------------------------
 * Adds a gift line item to the cart when the eligible subtotal crosses a
 * configured threshold, and renders a "buy X more" progress banner.
 *
 * Configured entirely from theme settings (Cart > Lahjakampanja), so a new
 * campaign needs no code change. See docs/specs/VAUVAN_PAIVA_LAHJAKAMPANJA.md
 *
 * IMPORTANT: this script does NOT make the gift free. Free-ness comes from an
 * automatic Buy X Get Y discount in admin (100% off, uses per order = 1). This
 * split is deliberate: the discount engine enforces the threshold server-side,
 * so the gift cannot be obtained by calling the cart API directly. If the
 * discount is missing or expired, verifyGiftDiscount() removes the line rather
 * than let the customer be charged for it.
 *
 * Campaign start/end dates are resolved in Liquid (theme.liquid), not here,
 * because the client clock is user-controlled.
 */
(function () {
  'use strict';

  const config = window.themeSettings?.giftCampaign;

  // Nothing to do when the campaign is off or no tier is fully configured.
  if (!config?.active || !config.tiers?.length) return;

  // Highest threshold first. Tiers STACK rather than override: reconcile()
  // walks every tier, so a cart over the top threshold receives every gift it
  // qualifies for (15 € → mini-harso, 100 € → mini-harso + harso). The admin
  // BXGY must grant the same number of items, or the extra gift arrives
  // undiscounted and verifyGiftDiscount() strips it again.
  const TIERS = [...config.tiers].sort((a, b) => b.threshold - a.threshold);
  const GIFT_IDS = TIERS.map((t) => String(t.variantId));
  const DEBOUNCE_MS = window.Woolman?.timing?.QUANTITY_CHANGE ?? 300;
  const DISMISSED_KEY = 'giftCampaignDismissed';

  let inProgress = false;
  let debounceTimer = null;
  // Set when a gift had to be removed for lacking its discount; suppresses the
  // banner so we never promise a gift that was just taken away.
  let undiscountedGift = false;

  /* ---------------------------------------------------------------------
   * Customer-removed gifts
   * A gift the customer deleted stays gone until the cart drops below the
   * threshold and crosses it again (decided 16.9.2026). sessionStorage keeps
   * this per-tab and clears itself when the tab closes.
   * ------------------------------------------------------------------ */
  function getDismissed() {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '[]'));
    } catch {
      return new Set();
    }
  }

  function setDismissed(set) {
    try {
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
    } catch {
      /* private browsing: fall back to re-adding, which is the safe direction */
    }
  }

  /* ---------------------------------------------------------------------
   * Cart helpers (cart.js keeps its own copies module-scoped)
   * ------------------------------------------------------------------ */
  async function getCart() {
    const response = await fetch(`${window.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
    return response.json();
  }

  function giftLines(cart) {
    return cart.items.filter((item) => GIFT_IDS.includes(String(item.variant_id)));
  }

  /**
   * Subtotal that counts toward the thresholds.
   *
   * Uses original_line_price (BEFORE line-level discounts), because that is
   * what Shopify's Buy X Get Y minimum-purchase requirement evaluates.
   * Verified empirically 22.9.2026 against the live discount: a cart of
   * 102,50 € before discounts / 86,70 € after still received the 100 €
   * gift discount.
   *
   * This must stay in sync with the admin discount. If the two disagree, the
   * customer gets the worse of both worlds: JS promises (or adds) a gift that
   * the discount engine refuses, and verifyGiftDiscount() then removes the
   * line AND marks it dismissed — the gift flickers away and does not return
   * until the threshold is crossed again.
   *
   * Gift lines are excluded so a gift can never push the cart over its own
   * threshold.
   */
  function eligibleSubtotal(cart) {
    return cart.items.reduce((sum, item) => {
      if (GIFT_IDS.includes(String(item.variant_id))) return sum;
      return sum + item.original_line_price;
    }, 0);
  }

  /** Convert a shop-currency threshold to the cart's presentment currency. */
  function toPresentmentCents(shopCents) {
    const rate = parseFloat(window.Shopify?.currency?.rate) || 1;
    return Math.round(shopCents * rate);
  }

  /* ---------------------------------------------------------------------
   * Reconciliation
   * ------------------------------------------------------------------ */
  async function addGift(variantId) {
    const response = await fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    // Out of stock is expected and must stay silent: the customer never asked
    // for this line, so an error toast would be confusing.
    if (!response.ok) {
      console.warn('[gift-campaign] could not add gift', variantId, response.status);
      return false;
    }
    return true;
  }

  async function setLineQuantity(key, quantity) {
    const response = await fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!response.ok) {
      console.warn('[gift-campaign] could not update line', key, response.status);
      return null;
    }
    return response.json();
  }

  /**
   * Safety net: a gift line with no discount applied means the discount is
   * missing, expired or misconfigured. Because gift products carry a real
   * price, leaving the line would charge the customer for a "free" gift, so we
   * remove it instead.
   *
   * The Cart AJAX API names this field `line_level_discount_allocations`
   * (`discount_allocations` is the GraphQL name). Both are checked so a future
   * API rename cannot silently turn this safety net into a gift-deleter.
   * final_line_price === 0 is the authoritative signal: the line costs nothing.
   */
  function isLineFree(line) {
    if (line.final_line_price === 0 || line.line_price === 0) return true;
    const allocations =
      line.line_level_discount_allocations ?? line.discount_allocations ?? [];
    return allocations.length > 0;
  }

  async function verifyGiftDiscount(cart) {
    let current = cart;
    for (const line of giftLines(current)) {
      if (isLineFree(line)) continue;

      console.warn(
        '[gift-campaign] gift has no discount applied — removing it so the customer is not charged. Check that the Buy X Get Y discount exists and is active.',
        line.variant_id
      );
      const updated = await setLineQuantity(line.key, 0);
      if (updated) current = updated;

      // Remember it, otherwise every cart:update re-adds and re-removes the
      // gift — a visible flicker and a pointless API loop.
      const dismissed = getDismissed();
      dismissed.add(String(line.variant_id));
      setDismissed(dismissed);
      undiscountedGift = true;
    }
    return current;
  }

  async function reconcile() {
    if (inProgress) return;
    inProgress = true;
    undiscountedGift = false;

    try {
      let cart = await getCart();
      const dismissed = getDismissed();
      const subtotal = eligibleSubtotal(cart);
      let changed = false;

      for (const tier of TIERS) {
        const variantId = String(tier.variantId);
        const threshold = toPresentmentCents(tier.threshold);
        const qualifies = subtotal >= threshold;
        const line = cart.items.find((item) => String(item.variant_id) === variantId);

        if (!qualifies) {
          // Below the threshold again: drop the gift and forget the dismissal
          // so it can be re-earned on the next crossing.
          if (line) {
            const updated = await setLineQuantity(line.key, 0);
            if (updated) {
              cart = updated;
              changed = true;
            }
          }
          if (dismissed.delete(variantId)) setDismissed(dismissed);
          continue;
        }

        if (!line) {
          if (dismissed.has(variantId)) continue; // customer removed it on purpose
          if (await addGift(variantId)) {
            cart = await getCart();
            changed = true;
          }
          continue;
        }

        // Quantity is always exactly 1; usesPerOrderLimit backs this server-side.
        if (line.quantity !== 1) {
          const updated = await setLineQuantity(line.key, 1);
          if (updated) {
            cart = updated;
            changed = true;
          }
        }
      }

      cart = await verifyGiftDiscount(cart);

      // Repaint the cart markup before the banner: the refreshed snippet ships
      // hidden, so painting first would wipe the message we just wrote.
      if (changed) await refreshCartMarkup();

      renderBanner(cart);
    } catch (error) {
      console.error('[gift-campaign] reconcile failed', error);
    } finally {
      inProgress = false;
    }
  }

  /**
   * Re-render the cart sections after a silent change.
   *
   * Uses cart-items.renderItems() rather than dispatching 'ajaxProduct:added':
   * that event calls renderAndShowItems(), which pops the cart drawer open —
   * wrong for a gift the customer did not explicitly add.
   */
  async function refreshCartMarkup() {
    const cartItems = document.getElementById('cart-items');
    if (!cartItems || typeof cartItems.updateCartSections !== 'function') return;

    // Fetch and swap the markup here rather than calling renderItems(): that
    // method does not return its own promise chain, so awaiting it resolves
    // before the DOM is replaced and the banner we paint next gets wiped.
    try {
      const response = await fetch(`${window.routes.cart_url}?section_id=main-cart`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      cartItems.updateCartSections(html);
    } catch (error) {
      console.warn('[gift-campaign] could not refresh cart markup', error);
    }
  }

  /* ---------------------------------------------------------------------
   * Progress banner
   * ------------------------------------------------------------------ */
  function renderBanner(cart) {
    const wrappers = document.querySelectorAll('[data-gift-threshold]');
    if (!wrappers.length) return;

    const subtotal = eligibleSubtotal(cart);
    const strings = window.cartStrings || {};

    // Lowest unreached tier drives the message.
    const ascending = [...TIERS].sort((a, b) => a.threshold - b.threshold);
    const next = ascending.find((tier) => subtotal < toPresentmentCents(tier.threshold));

    wrappers.forEach((el) => {
      const target = el.querySelector('[data-gift-threshold-message]') || el;

      // Empty cart, or a gift was pulled for lacking its discount: say nothing
      // rather than advertise a gift the customer will not receive.
      if (cart.item_count === 0 || undiscountedGift) {
        el.hidden = true;
        return;
      }

      if (!next) {
        const all = strings.gift_threshold_reached;
        el.hidden = !all;
        el.classList.add('threshold-reached');
        if (all) target.innerHTML = all;
        return;
      }

      const missing = toPresentmentCents(next.threshold) - subtotal;
      const template = strings.gift_threshold_progress;
      if (!template) {
        el.hidden = true;
        return;
      }

      el.hidden = false;
      el.classList.remove('threshold-reached');
      target.innerHTML = template
        .replace('[amount]', window.Woolman?.Utils?.formatMoney?.(missing) ?? '')
        .replace('[gift]', next.title);
    });
  }

  /* ---------------------------------------------------------------------
   * Wiring
   * ------------------------------------------------------------------ */
  function scheduleReconcile() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  // Remember gifts the customer removes themselves.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('cart-remove-button');
    if (!button) return;

    const row = button.closest('[data-gift-variant-id]');
    const variantId = row?.dataset.giftVariantId;
    if (!variantId || !GIFT_IDS.includes(String(variantId))) return;

    const dismissed = getDismissed();
    dismissed.add(String(variantId));
    setDismissed(dismissed);
  });

  // Both events matter. cart.js and component-product-form.js fire
  // 'ajaxProduct:added' when a product is added (refreshCartSections() uses it
  // too); only component-bundle-upsell.js fires 'cart:update'. Listening to
  // 'cart:update' alone meant the gift appeared only on the NEXT cart action —
  // the customer crossed the threshold and saw nothing until they touched the
  // cart again. Reported 22.9.2026.
  //
  // Safe against loops: refreshCartMarkup() calls cart-items.renderItems()
  // directly rather than dispatching, and reconcile() is debounced and guarded
  // by inProgress.
  document.addEventListener('cart:update', scheduleReconcile);
  document.addEventListener('ajaxProduct:added', scheduleReconcile);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleReconcile, { once: true });
  } else {
    scheduleReconcile();
  }
})();
